/**
 * UX-04: shared `/session-logs` deep-link builder. Used by Today (full
 * session context) and the epic drawer's Raw logs tab (task id only) so
 * the two "view logs" entry points never drift apart.
 *
 * URL hygiene: callers must pass the reduced task ID, never a full ARN —
 * `taskIdFromArn` does that reduction from whatever `taskArn` they have on
 * hand.
 */

export interface SessionLogsLinkParams {
  task: string;
  agent?: string;
  repo?: string;
  story?: string;
  /** GitHub issue number for same-repo epics. Omit (or pass <= 0) when unknown. */
  epic?: number | string;
  /** Cross-repo epic handle — takes precedence over `epic` on the logs page. */
  epicRef?: string;
  outcome?: string | null;
  startedAt?: string;
  finishedAt?: string | null;
}

/** AWS task ARNs embed the account id / region / cluster — never put one in a URL. */
export function taskIdFromArn(taskArn: string): string {
  return taskArn.includes('/') ? (taskArn.split('/').pop() as string) : taskArn;
}

/**
 * Builds the `/session-logs` query string. Returns `null` when there's no
 * task to look up, mirroring the callers' existing "no link" behaviour.
 */
export function sessionLogsHref(params: SessionLogsLinkParams): string | null {
  if (!params.task) return null;
  const qs = new URLSearchParams({ task: params.task });
  if (params.agent) qs.set('agent', params.agent);
  if (params.repo) qs.set('repo', params.repo);
  if (params.story) qs.set('story', params.story);
  if (params.epic !== undefined && params.epic !== null && `${params.epic}` !== '' && params.epic !== 0) {
    qs.set('epic', String(params.epic));
  }
  if (params.epicRef) qs.set('epicRef', params.epicRef);
  if (params.outcome) qs.set('outcome', params.outcome);
  if (params.startedAt) qs.set('started', params.startedAt);
  if (params.finishedAt) qs.set('finished', params.finishedAt);
  return `/session-logs?${qs}`;
}
